import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", trn: "", address: "", email: "", phone: "", contact_person: "" };

export default function Vendors() {
  const { isAdmin } = useAuth();
  const [vendors, setVendors] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vendors").then(({ data }) => setVendors(data)).catch((e) => { toast.error(apiError(e)); setVendors([]); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ ...empty, ...v }); setOpen(true); };
  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Vendor name is required");
    setSaving(true);
    const payload = {
      name: form.name.trim(), trn: form.trn, address: form.address,
      email: form.email, phone: form.phone, contact_person: form.contact_person,
    };
    try {
      if (editing) {
        await api.put(`/vendors/${editing.id}`, payload);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (v) => {
    try {
      await api.delete(`/vendors/${v.id}`);
      toast.success("Vendor deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">Vendors &amp; Payees</h2>
          <p className="text-sm text-slate-500">Reusable payee records — no re-typing on each voucher.</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="add-vendor-btn"><Plus className="w-4 h-4" /> Add Vendor</Button>
      </div>

      {vendors === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map((v) => (
            <Card key={v.id} className="p-5 border-slate-200 dark:border-slate-800 flex flex-col" data-testid={`vendor-card-${v.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="font-semibold truncate">{v.name}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {isAdmin && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`delete-vendor-${v.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {v.name}?</AlertDialogTitle>
                          <AlertDialogDescription>Vendors used by existing vouchers cannot be deleted.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(v)} className="bg-rose-600 hover:bg-rose-500">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-500">
                {v.trn && <div className="font-mono text-xs">TRN: {v.trn}</div>}
                {v.address && <div className="truncate">{v.address}</div>}
                {v.email && <div className="truncate">{v.email}</div>}
                {v.phone && <div className="font-mono text-xs">{v.phone}</div>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="vendor-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit vendor" : "Add vendor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="vendor-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">TRN</Label>
                <Input value={form.trn} onChange={(e) => set("trn", e.target.value)} className="mt-1.5 font-mono" data-testid="vendor-trn" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Phone</Label>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" data-testid="vendor-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="vendor-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Address</Label>
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5" data-testid="vendor-address" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-vendor-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Update" : "Add vendor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
